import { HStack, Text } from '@chakra-ui/react'
import { ContentNode, DataTypeConfigUtils } from '@helo/cms'
import { If, pipe } from '@helo/utils'
import { kebabCase } from 'lodash'

import { HeadingError } from '../../components/Errors/HeadingError'
import { ArrowBackIcon } from '../../components/Icons/ArrowBackIcon'
import { Link, LinkProps } from '../../components/Link/Link'
import { Skeleton } from '../../components/SkeletonLoader'
import { TypeRenderer } from '../../datatypes/TypeRenderer'
import { createWidget } from '../widget-utils'

export type HeaderBackLinkWidgetProps = Omit<LinkProps, 'to'> & {
  url?: string
  caption?: string
  subCaption?: string | ContentNode
  isLoading?: boolean
}

const getFallbackUrlFromCaption = (caption?: string): string => {
  if (!caption) return '/'

  const match = caption.match(/^([\w\s]+)\s*\//)
  if (match) {
    // TODO: using kebabCase to fix path name so URL works is not ideal, needs to be fixed
    const path = kebabCase(match[1].trim().toLowerCase())
    return `/${path}`
  }

  return '/'
}

export const HeaderBackLinkWidget = ({
  children,
  caption,
  subCaption,
  url,
  isLoading,
  ...props
}: HeaderBackLinkWidgetProps) => {
  const isSubCaptionContentNode =
    typeof subCaption === 'object' && subCaption && 'data' in subCaption

  const subCaptionContentNodeWithStyles =
    isSubCaptionContentNode &&
    pipe(
      subCaption,
      If.then(
        contentNode => contentNode.kind === 'FIELD',
        DataTypeConfigUtils.getOfType(),
      ),
      DataTypeConfigUtils.updateState({
        linkUrl: false,
        styles: { fontWeight: '600' },
      }),
    )

  const getNavigationTarget = () => {
    if (url) return url
    if (window.history.length <= 1) {
      return getFallbackUrlFromCaption(caption)
    }
    return -1 as unknown as string
  }

  return (
    <HStack as="span" gap="5px" alignItems="center">
      <Link
        color="presets.turquoise.60"
        fontSize="14px"
        to={getNavigationTarget()}
        data-testid="HeaderBackLinkWidget"
        display="flex"
        alignItems="center"
        gap="5px"
        {...props}
      >
        <ArrowBackIcon
          boxSize={2}
          width={6}
          height={6}
          mb={1}
          color="presets.turquoise.60"
        />
        <Text
          fontWeight="600"
          color="presets.turquoise.55"
          data-testid="HeaderBackLinkWidget-link-text"
        >
          {caption}
        </Text>
      </Link>
      {isSubCaptionContentNode ? (
        <Text
          color="presets.turquoise.100"
          data-testid="HeaderBackLinkWidget-current-path"
        >
          <TypeRenderer
            contentNode={subCaptionContentNodeWithStyles as ContentNode}
            isLoading={isLoading}
          />
        </Text>
      ) : (
        <Skeleton isLoading={isLoading && !subCaption} h="12px" w="100px">
          <Text
            color="presets.turquoise.100"
            data-testid="HeaderBackLinkWidget-current-path"
          >
            {subCaption}
          </Text>
        </Skeleton>
      )}
    </HStack>
  )
}

export const HeaderBackLinkWidgetDefinition = createWidget(
  HeaderBackLinkWidget,
  {
    type: 'HeaderBackLink',
    errorRenderer: HeadingError,
  },
)
